/**
 * Input Component
 * Reusable input component with validation and different types
 */

import { forwardRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Input Component
 * @param {Object} props - Component props
 * @param {string} props.type - Input type
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {boolean} props.error - Whether input has error
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.label - Input label
 * @param {string} props.helperText - Helper text
 * @param {string} props.errorMessage - Error message
 */
const Input = forwardRef(({
  className,
  type = 'text',
  placeholder,
  value,
  onChange,
  disabled = false,
  error = false,
  label,
  helperText,
  errorMessage,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  
  const inputType = type === 'password' && showPassword ? 'text' : type;
  const hasError = error || !!errorMessage;

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {label}
        </label>
      )}
      
      <div className="relative">
        <input
          type={inputType}
          className={cn(
            // Base styles
            'flex h-10 w-full rounded-md border px-3 py-2',
            // Light mode
            'border-gray-300 bg-white text-gray-900 placeholder:text-gray-500',
            // Dark mode
            'dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-400',
            // Focus
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
            // Disabled
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-gray-900 dark:disabled:text-gray-500',
            
            // Error styles
            hasError && 'border-red-300 focus-visible:ring-red-500',
            
            className
          )}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          ref={ref}
          {...props}
        />
        
        {type === 'password' && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3"
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
          >
            <span className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300">
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </span>
          </button>
        )}
      </div>
      
      {(helperText || errorMessage) && (
        <p className={cn(
          'mt-1 text-sm',
          hasError ? 'text-red-600' : 'text-gray-500'
        )}>
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;